'use client'

import cx from 'classnames'
import { FC } from 'react'

import styles from './styles.module.scss'
import { SimpleInterestCalculator } from './Calculators/SimpleInterestCalculator'
import { CompoundInterestCalculator } from './Calculators/CompoundInterestCalculator'

import { Panel, PanelLayout } from '@/components/Panel/Panel'
import { PanelItem } from '@/components/Panel/PanelItem'
import { Colour } from '@/components/constants/colours'
import { useIsBlogPage } from '@/hooks/useIsBlogPage'
import { PanelTitles } from '@/components/PanelTitles'
import { CmsPageSectionBase } from '@/components/ComposedPage/types'
import { SectionPaddingDefault } from '@/components/constants/layouting'

export enum CalculatorType {
  SIMPLE_INTEREST = 'simpleInterest',
  COMPOUND_INTEREST = 'compoundInterest',
}

export enum LimitDisplayOptions {
  DESKTOP = 'desktop',
  MOBILE = 'mobile',
}

export interface CmsCalculatorSection extends CmsPageSectionBase {
  fields: CmsPageSectionBase['fields'] & {
    title?: string
    calculatorTitle?: string
    calculatorBodyCopy?: string
    footnote?: string
    backgroundColour: Colour
    calculatorType?: string
  }
}

const calculatorTypeMap: Record<CalculatorType, FC> = {
  [CalculatorType.SIMPLE_INTEREST]: SimpleInterestCalculator,
  [CalculatorType.COMPOUND_INTEREST]: CompoundInterestCalculator,
}

// Gatsby-style component that receives spread section props
export const CalculatorSection: FC<CmsCalculatorSection> = ({ fields }) => {
  const {
    title,
    calculatorTitle,
    calculatorBodyCopy,
    footnote,
    backgroundColour,
    calculatorType,
    sectionPaddingTop = SectionPaddingDefault.TOP,
    sectionPaddingBottom = SectionPaddingDefault.BOTTOM,
  } = fields

  const Calculator =
    calculatorTypeMap[calculatorType as CalculatorType] ||
    SimpleInterestCalculator
  const isBlogPage = useIsBlogPage()

  return (
    <Panel
      className={cx(
        styles.calculatorSection,
        styles[sectionPaddingTop],
        styles[sectionPaddingBottom],
      )}
      layout={PanelLayout.full}
      colour={Colour[backgroundColour]}
      short
    >
      <PanelItem className={cx({ [styles.blogItem]: isBlogPage })}>
        <PanelTitles title={title} />
        <div className={cx(styles.content)}>
          <div
            className={cx(styles.calculatorPane, {
              [styles.isBlog]: isBlogPage,
            })}
          >
            <div className={styles.text}>
              <h5 className={styles.calculatorTitle}>{calculatorTitle}</h5>
              <p>{calculatorBodyCopy}</p>
            </div>
            <div className={styles.calculator}>
              {Calculator ? <Calculator /> : null}
            </div>
          </div>
          <div className={styles.footnote}>{footnote}</div>
        </div>
      </PanelItem>
    </Panel>
  )
}

export default CalculatorSection
